import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PhilRole, ROLE_LABELS, useRole } from './RoleContext';
import './phil.css';

/** Role-specific top-nav links. The route each points to is the persona's home. */
const NAV_BY_ROLE: Record<PhilRole, { label: string; path: string; match: string | string[] }[]> = {
  applicant: [
    { label: 'Dashboard', path: '/apply', match: '/apply' },
    { label: 'My Application', path: '/apply/section-1', match: ['/apply/section', '/apply/review'] },
    { label: 'Resources', path: '/apply/resources', match: '/apply/resources' },
  ],
  evaluator: [
    { label: 'Evaluate', path: '/evaluate', match: '/evaluate' },
  ],
  admin: [
    { label: 'Dashboard', path: '/dashboard', match: '/dashboard' },
    { label: 'Pipeline', path: '/admin/pipeline', match: ['/admin/pipeline', '/admin/applications'] },
    { label: 'Partners', path: '/feedback-priorities', match: '/feedback-priorities' },
    { label: 'Reports', path: '/admin/reports', match: '/admin/reports' },
    { label: 'Settings', path: '/admin/settings', match: ['/admin/settings', '/admin/pipeline-columns'] },
  ],
};

export default function PhilLayout({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { role, displayName, initials, logout } = useRole();

  const links = NAV_BY_ROLE[role];

  // The pipeline table needs the full screen width; every other page keeps the
  // comfortable 1100px reading cap. (Excludes /admin/applications/:id detail.)
  const wide = location.pathname.startsWith('/admin/pipeline');

  // Highlight the single nav link whose `match` is the longest prefix of the
  // current path (avoids overlapping prefixes lighting up multiple links).
  const matchLen = (l: { match: string | string[] }): number => {
    const prefixes = Array.isArray(l.match) ? l.match : [l.match];
    return prefixes
      .filter(m => m !== '__none__' && m !== '__none2__' && location.pathname.startsWith(m))
      .reduce((max, m) => Math.max(max, m.length), 0);
  };
  const bestMatch = links
    .filter(l => matchLen(l) > 0)
    .sort((a, b) => matchLen(b) - matchLen(a))[0];

  return (
    <div className="phil-app">
      <nav className="phil-nav">
        <img
          src="/fintechlab-logo.png"
          alt="FinTech Lab"
          className="phil-brand-logo"
          onClick={() => navigate(links[0].path)}
        />
        {links.map(link => {
          const active = link === bestMatch;
          return (
            <div
              key={link.label}
              className={`phil-nav-link${active ? ' active' : ''}`}
              onClick={() => navigate(link.path)}
            >
              {link.label}
            </div>
          );
        })}
        <div className="phil-nav-right">
          <div className="phil-role-switch">
            <span className="phil-role-label">{ROLE_LABELS[role]}</span>
            {displayName && <span className="phil-user-email">{displayName}</span>}
          </div>
          <button className="phil-logout" onClick={logout}>Sign out</button>
          <div className="phil-avatar">{initials}</div>
        </div>
      </nav>
      <div className={`phil-content${wide ? ' phil-content-wide' : ''}`}>{children}</div>
      <PhilFooter />
    </div>
  );
}

/** App-wide footer with the Accenture privacy statement link. */
export function PhilFooter() {
  return (
    <footer className="phil-footer">
      <span>© {new Date().getFullYear()} Fintech Innovation Lab</span>
      <a
        href="https://www.accenture.com/en/support/privacy-policy"
        target="_blank"
        rel="noopener noreferrer"
      >
        Privacy Statement
      </a>
    </footer>
  );
}
